<script setup lang="ts">
import { computed } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { erpIntegrationApi } from '@/api/erp-integration';
import { careerLabel } from '@/utils/career-metadata';
import { useResponsiveTable } from '@/composables/useResponsiveTable';
import { employeeReason } from './erp-messages';

// Employees Kuido can't bring in as they are (fixed in Odoo; the next
// synchronization brings them), and caregivers who can't get into the app.

const { tableProps } = useResponsiveTable({ desktopItemsPerPage: 25 });

const { data, isPending } = useQuery({
  queryKey: ['erpIntegration', 'employees'],
  queryFn: erpIntegrationApi.employees,
});

const reviews = computed(() => data.value?.reviews ?? []);
const unready = computed(() => data.value?.unready ?? []);

const reviewHeaders = [
  { title: 'Empleado en Odoo', key: 'name' },
  { title: 'Motivo', key: 'reason', sortable: false },
  { title: 'Profesional en Kuido', key: 'caregiver', sortable: false },
];
const unreadyHeaders = [
  { title: 'Profesional', key: 'name' },
  { title: 'Especialidad', key: 'career' },
];
</script>
<template>
  <div class="pa-4">
    <h2 class="text-h6 mb-1">
      Empleados por corregir en Odoo
    </h2>
    <p class="mb-4 text-body-2 text-medium-emphasis">
      Kuido no puede traerlos como están. Corrígelos en Odoo: la próxima sincronización los trae.
    </p>
    <v-data-table
      v-bind="tableProps"
      :header-props="{ class: 'font-weight-bold' }"
      :headers="reviewHeaders"
      :items="reviews"
      :loading="isPending"
      loading-text="Cargando los empleados..."
      no-data-text="No hay empleados por corregir"
      density="compact"
    >
      <template #[`item.name`]="{ item }">
        <div>{{ item.name ?? '—' }}</div>
        <div class="text-caption text-medium-emphasis">
          ID Odoo {{ item.remoteId }}
        </div>
      </template>
      <template #[`item.reason`]="{ item }">
        <v-chip
          size="small"
          color="warning"
          prepend-icon="mdi-alert-circle"
        >
          {{ employeeReason(item.reason) }}
        </v-chip>
      </template>
      <template #[`item.caregiver`]="{ item }">
        <a
          v-if="item.caregiver"
          :href="`/caregivers/${item.caregiver.id}`"
        >{{ item.caregiver.name }}</a>
        <span v-else>—</span>
      </template>
    </v-data-table>

    <h2 class="text-h6 mt-6 mb-1">
      Profesionales sin acceso a la app
    </h2>
    <p class="mb-4 text-body-2 text-medium-emphasis">
      Vienen de Odoo sin correo ni teléfono, así que no pueden marcar asistencia. Agrégalos en
      Odoo.
    </p>
    <v-data-table
      v-bind="tableProps"
      :header-props="{ class: 'font-weight-bold' }"
      :headers="unreadyHeaders"
      :items="unready"
      :loading="isPending"
      loading-text="Cargando los profesionales..."
      no-data-text="Todos los profesionales pueden entrar a la app"
      density="compact"
    >
      <template #[`item.name`]="{ item }">
        <a :href="`/caregivers/${item.id}`">{{ item.name }}</a>
      </template>
      <template #[`item.career`]="{ item }">
        {{ careerLabel(item.career) }}
      </template>
    </v-data-table>
  </div>
</template>
